import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Globe, Plus, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import type { TranslationChoice } from '@/lib/translation-preferences';
import { componentLogger } from '@/lib/logging';

interface TranslationChoiceBannerProps {
  /** The browser language that is not supported */
  browserLanguage: string;
  /** Display name of the browser language */
  browserLanguageDisplayName: string;
  /** Whether the banner is visible */
  isVisible: boolean;
  /** Callback when user makes a choice */
  onChoice: (_choice: TranslationChoice) => void;
  /** Callback to close the banner without making a choice */
  onClose: () => void;
  /** Whether choices are being processed */
  isProcessing?: boolean;
  /** Custom CSS classes */
  className?: string;
}

export function TranslationChoiceBanner({
  browserLanguage,
  browserLanguageDisplayName,
  isVisible,
  onChoice,
  onClose,
  isProcessing = false,
  className = ''
}: TranslationChoiceBannerProps) {
  const { t } = useTranslation();
  const [isRequestingLanguage, setIsRequestingLanguage] = useState(false);
  const [hasRequestedLanguage, setHasRequestedLanguage] = useState(false);

  // Check if language has been requested before
  useEffect(() => {
    const storageKey = `language-request-${browserLanguage}`;
    const requestData = localStorage.getItem(storageKey);

    if (requestData) {
      try {
        const { timestamp } = JSON.parse(requestData);
        // Allow re-requesting after 30 days
        const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);

        if (timestamp > thirtyDaysAgo) {
          setHasRequestedLanguage(true);
        } else {
          // Remove expired request data
          localStorage.removeItem(storageKey);
        }
      } catch {
        // Invalid data, remove it
        localStorage.removeItem(storageKey);
      }
    }
  }, [browserLanguage]);

  const handleRequestLanguage = async () => {
    setIsRequestingLanguage(true);

    try {
      const response = await fetch(
        `${import.meta.env['VITE_PUBLIC_SUPABASE_URL']}/functions/v1/log-language-request`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            language_code: browserLanguage,
            language_name: browserLanguageDisplayName
          })
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      // Store successful request in localStorage
      const storageKey = `language-request-${browserLanguage}`;
      const requestData = {
        language_code: browserLanguage,
        language_name: browserLanguageDisplayName,
        timestamp: Date.now()
      };
      localStorage.setItem(storageKey, JSON.stringify(requestData));

      // Update component state
      setHasRequestedLanguage(true);

      toast({
        description: t('translation.messages.languageRequested', { language: browserLanguageDisplayName })
      });

    } catch (error) {
      componentLogger.error('Translation language request failed', '[translation-banner] Failed to request language:', (error as Error).message || 'Unknown error');
      toast({
        title: 'Error',
        description: t('translation.errors.requestFailed'),
        variant: 'destructive'
      });
    } finally {
      setIsRequestingLanguage(false);
    }
  };

  if (!isVisible) {
    return null;
  }

  const handleChoice = (choice: TranslationChoice) => {
    if (!isProcessing) {
      onChoice(choice);
    }
  };

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 transform ${
        isVisible ? 'translate-y-0' : '-translate-y-full'
      } ${className}`}
      role="banner"
      aria-live="polite"
    >
      <Card className="mx-4 mt-4 border-l-4 border-l-blue-500 shadow-lg">
        <CardContent className="p-4">
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-blue-600" />
              <h3 className="font-semibold text-lg text-gray-900">
                {t('translation.banner.title')}
              </h3>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              disabled={isProcessing}
              className="h-8 w-8 p-0"
              aria-label={t('translation.banner.close')}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Main Message */}
          <div className="mb-4">
            <p className="text-gray-700 mb-2">
              {t('translation.banner.notAvailableYet', { language: browserLanguageDisplayName })}
            </p>
          </div>

          {/* Choice Buttons - Supported Languages */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            {/* Use English */}
            <Button
              onClick={() => handleChoice('use_english')}
              disabled={isProcessing}
              variant="outline"
              className="h-auto py-3"
            >
              <div className="text-center">
                <div className="font-medium">English</div>
                <div className="text-xs text-gray-500">
                  {t('translation.banner.useEnglish')}
                </div>
              </div>
            </Button>

            {/* Use Spanish */}
            <Button
              onClick={() => handleChoice('es')}
              disabled={isProcessing}
              variant="outline"
              className="h-auto py-3"
            >
              <div className="text-center">
                <div className="font-medium">Español</div>
                <div className="text-xs text-gray-500">
                  {t('translation.banner.useSpanish')}
                </div>
              </div>
            </Button>

            {/* Use French */}
            <Button
              onClick={() => handleChoice('fr')}
              disabled={isProcessing}
              variant="outline"
              className="h-auto py-3"
            >
              <div className="text-center">
                <div className="font-medium">Français</div>
                <div className="text-xs text-gray-500">
                  {t('translation.banner.useFrench')}
                </div>
              </div>
            </Button>

            {/* Use Swedish */}
            <Button
              onClick={() => handleChoice('sv')}
              disabled={isProcessing}
              variant="outline"
              className="h-auto py-3"
            >
              <div className="text-center">
                <div className="font-medium">Svenska</div>
                <div className="text-xs text-gray-500">
                  {t('translation.banner.useSwedish')}
                </div>
              </div>
            </Button>
          </div>

          {/* Request Language Option */}
          <div className="mb-4">
            <Button
              onClick={handleRequestLanguage}
              disabled={hasRequestedLanguage || isRequestingLanguage || isProcessing}
              variant="outline"
              className={`w-full h-auto py-3 px-4 flex items-center justify-center gap-2 transition-colors duration-200 ${
                hasRequestedLanguage
                  ? 'bg-green-50 border-green-200 text-green-700 hover:bg-green-50'
                  : ''
              }`}
            >
              {hasRequestedLanguage ? (
                <Check className="h-4 w-4 text-green-600" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              <span className="font-medium">
                {hasRequestedLanguage
                  ? `${browserLanguageDisplayName} Requested ✓`
                  : t('translation.banner.requestLanguage', { language: browserLanguageDisplayName })
                }
              </span>
              {isRequestingLanguage && (
                <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-gray-600 rounded-full" />
              )}
            </Button>
          </div>

          {/* Footer Information */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 pt-3 border-t border-gray-100">
            <div className="text-xs text-gray-500">
              {t('translation.banner.rememberChoice', { days: 30 })}
            </div>

            <Badge variant="secondary" className="text-xs">
              {t('translation.messages.availableLanguages')}
            </Badge>
          </div>

          {/* Loading State */}
          {isProcessing && (
            <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-lg">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                Processing...
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default TranslationChoiceBanner;